/* eslint-disable react/forbid-prop-types,no-undef */
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';

import { v4 as uuidv4 } from 'uuid';
import PropTypes from 'prop-types';

import { useSetRecoilState } from 'recoil';
import useHttpRequest from '../../../../api/HttpRequest';

import CashoutProcessing from './CashoutProcessing';
import CashoutSuccess from './CashoutSuccess';
import CashoutError from './CashoutError';
import CashoutConfirmation from './CashoutConfirmation';
import ClaimRewardsStatuses from '../../../common/Constants';
import ClaimRewardsResultAtom from '../../../../../recoil/cashout/ClaimRewardsResultAtom';

export default function CashoutConfirmationModal({
  payAccount, selectedPackage,
  onEmailChanged, onCountryCodeChanged, onPaymentMethodUpdate, onContinue, onClose,
}) {
  const httpRequest = useHttpRequest();

  const setClaimRewardsResult = useSetRecoilState(ClaimRewardsResultAtom);
  const [status, setStatus] = useState(ClaimRewardsStatuses.idle);
  const [payMethod, setPayMethod] = useState(null);
  const [cashoutError, setCashoutError] = useState({
    titleKey: 'cashout_cash_out_error',
    descriptionKey: 'cashout_cash_out_error_description',
  });

  const createPayoutRequest = useCallback(async (evt) => {
    evt?.preventDefault?.();

    const {
      provider,
      email,
      countryCode,
    } = payAccount;

    // Prevent multiple submissions
    if (status !== ClaimRewardsStatuses.idle) return;

    setStatus(ClaimRewardsStatuses.processing);
    try {
      const result = await httpRequest.send(`/web-payments/cash-reward/orders/${uuidv4()}`, {
        method: 'PUT',
        body: {
          productId: selectedPackage.productId,
          provider,
          ...email && { destination: email },
          ...countryCode && { countryCode },
        },
        additionalHeaders: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });

      console.log('Cashout order submitted successfully: ', result);
      setStatus(ClaimRewardsStatuses.success);
      setClaimRewardsResult({
        status: ClaimRewardsStatuses.success,
        updatedAt: Date.now(),
      });
    } catch (err) {
      console.log('Unable to submit cashout order due to: ', err);

      setClaimRewardsResult({
        status: ClaimRewardsStatuses.error,
        updatedAt: Date.now(),
      });

      const response = err;

      if (!response || typeof response.status !== 'number') {
        setCashoutError({
          titleKey: 'cashout_cash_out_error',
          descriptionKey: 'cashout_cash_out_error_description',
        });
        setStatus(ClaimRewardsStatuses.error);
        return;
      }

      const responseBody = typeof response.clone === 'function'
        ? await response.clone().json().catch(() => null)
        : null;

      if (response.status === 502) {
        window.location = '/maintenance';
        return;
      }

      if (response.status === 401) {
        window.location = '/unauthenticated';
        return;
      }

      if (response.status === 429 && responseBody?.type === 'order-limit-exceeded') {
        setCashoutError({
          titleKey: 'cashout_cash_out_limit',
          descriptionKey: 'cashout_cash_out_limit_description',
        });
        setStatus(ClaimRewardsStatuses.error);
        return;
      }

      if (response.status === 422 && responseBody?.type === 'invalid-parameter-email') {
        setCashoutError({
          titleKey: 'cashout_cash_out_error',
          descriptionKey: 'cashout_email_address_invalid',
        });
        setStatus(ClaimRewardsStatuses.error);
        return;
      }

      if (response.status === 402) {
        setCashoutError({
          titleKey: 'cashout_cash_out_insufficient_funds',
          descriptionKey: 'cashout_cash_out_insufficient_funds_description',
        });
        setStatus(ClaimRewardsStatuses.error);
        return;
      }

      setCashoutError({
        titleKey: 'cashout_cash_out_error',
        descriptionKey: 'cashout_cash_out_error_description',
      });
      setStatus(ClaimRewardsStatuses.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payAccount, selectedPackage, status]);

  const content = useMemo(() => {
    switch (status) {
      case ClaimRewardsStatuses.processing:
        return <CashoutProcessing />;
      case ClaimRewardsStatuses.success:
        return <CashoutSuccess paymentProvider={payAccount.provider} onContinue={onContinue} />;
      case ClaimRewardsStatuses.error:
        return (
          <CashoutError
            titleKey={cashoutError.titleKey}
            descriptionKey={cashoutError.descriptionKey}
            onClose={onClose}
          />
        );
      default:
        return (
          <CashoutConfirmation
            payAccount={payAccount}
            payMethod={payMethod}
            selectedPackage={selectedPackage}
            onEmailChanged={(newEmail) => {
              onEmailChanged(newEmail);
            }}
            onCountryCodeChanged={(newCountryCode) => {
              onCountryCodeChanged(newCountryCode);
            }}
            onSubmit={async (evt) => {
              await createPayoutRequest(evt);
            }}
            onPaymentMethodUpdate={onPaymentMethodUpdate}
            onClose={onClose}
          />
        );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payAccount, status, selectedPackage, cashoutError]);

  useEffect(() => {
    const provider = payAccount?.provider;
    if (!provider) return;

    const fetchPaymentMethodDetails = async () => {
      setStatus(ClaimRewardsStatuses.processing);

      try {
        const paymentMethodDetails = await httpRequest.send(
          `/web-payments/cash-reward/payment-method/${provider}`,
        );
        setPayMethod(paymentMethodDetails);
      } catch (err) {
        console.log('Unable to fetch payment method details', err);
      } finally {
        setStatus(ClaimRewardsStatuses.idle);
      }
    };

    fetchPaymentMethodDetails();
    // eslint-disable-next-line
  }, [payAccount?.provider]);

  return (
    <div
      id="cashout-confirmation"
      className="phoenix-simple-dialog-view modal member-diamonds-cash-confirm show"
      aria-hidden="false"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cashout-confirmation-title"
    >
      <div>
        { content }
      </div>
    </div>
  );
}
CashoutConfirmationModal.propTypes = {
  payAccount: PropTypes.object.isRequired,
  selectedPackage: PropTypes.shape({
    productId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }).isRequired,
  onEmailChanged: PropTypes.func.isRequired,
  onCountryCodeChanged: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
  onPaymentMethodUpdate: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};
